import { memo, useState } from 'react';
import { LeadStatusPickerField } from 'shared/ui/formik-fields/lead-status-picker-field';
import { LeadBudgetPickerField } from 'shared/ui/formik-fields/lead-budget-picker-field';
import { LeadQualityPickerField } from 'shared/ui/formik-fields/lead-quality-picker-field';
import { Form, Formik, Field } from 'formik';
import { Stack, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText } from '@mui/material';
import { CardPaper } from 'shared/ui/card-paper';
import { BulkFormData } from '../create/form';
import { UserPickerField } from '../../user/user-picker';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useSnack } from 'shared/ui/snack';
import { useBulkUpdateLeads } from 'entities/lead/leads.hook';
import { LeadBulkUpdateDto } from '@sr/dto';

const initialValues: BulkFormData = {
  statusId: '',
  budgetId: '',
  qualityId: '',
  userId: ''
};
type LeadBulkUpdateProps = {
  selectedIds: number[];
  excludedIds?: number[];
  all?: boolean;
  filter?: string;
  selectedCount: number;
  onUpdated?: () => Promise<void> | void;
};

type PendingUpdate = {
  dto: LeadBulkUpdateDto;
  changes: Array<{ label: string; value: string }>;
};

type ChangeLabels = Partial<Record<keyof BulkFormData, string>>;

const toBulkUpdateDto = (ids: number[], data: BulkFormData, all?: boolean, filter?: string, excludedIds?: number[]): LeadBulkUpdateDto => {
  const mapToNumber = (value?: number | '' | null) => {
    if (value === undefined || value === '' || value === null) return undefined;
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? undefined : parsedValue;
  };

  const dto: LeadBulkUpdateDto = all ? { all: true, filter, excludedIds } : { ids };

  const statusId = mapToNumber(data.statusId);
  if (statusId !== undefined) dto.statusId = statusId;

  const budgetId = mapToNumber(data.budgetId);
  if (budgetId !== undefined) dto.budgetId = budgetId;

  const qualityId = mapToNumber(data.qualityId);
  if (qualityId !== undefined) dto.qualityId = qualityId;

  const managerId = mapToNumber(data.userId);
  if (managerId !== undefined) dto.managerId = managerId;

  return dto;
};

const LeadUpdate = ({ selectedIds, excludedIds, all, filter, selectedCount, onUpdated }: LeadBulkUpdateProps) => {
  const { showError, showSuccess } = useSnack();
  const update = useBulkUpdateLeads();
  const [changeLabels, setChangeLabels] = useState<ChangeLabels>({});
  const [pendingUpdate, setPendingUpdate] = useState<PendingUpdate | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const setChangeLabel = (field: keyof BulkFormData, value?: string) => {
    setChangeLabels((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (data: BulkFormData) => {
    const dto = toBulkUpdateDto(selectedIds, data, all, filter, excludedIds);
    const changes = [
      dto.statusId !== undefined ? { label: 'Статус', value: changeLabels.statusId ?? String(dto.statusId) } : null,
      dto.budgetId !== undefined ? { label: 'Бюджет', value: changeLabels.budgetId ?? String(dto.budgetId) } : null,
      dto.qualityId !== undefined ? { label: 'Качество', value: changeLabels.qualityId ?? String(dto.qualityId) } : null,
      dto.managerId !== undefined ? { label: 'Менеджер', value: dto.managerId === 0 ? 'Не выбран' : changeLabels.userId ?? String(dto.managerId) } : null
    ].filter((change): change is { label: string; value: string } => change !== null);

    if (!changes.length) {
      showError('Выберите хотя бы одно изменение');
      return;
    }

    setPendingUpdate({ dto, changes });
  };

  const handleConfirm = async () => {
    if (!pendingUpdate) return;

    setIsUpdating(true);
    try {
      const result = await update(pendingUpdate.dto);
      if (pendingUpdate.dto.all && !result) {
        showError('Сервер не подтвердил массовое обновление. Нужно обновить backend и повторить действие');
        return;
      }

      if (result?.targetCount === 0) {
        showError('Лиды для обновления не найдены');
        return;
      }

      await onUpdated?.();
      setPendingUpdate(null);
      showSuccess(result ? `Данные успешно обновлены. Обновлено: ${result.updatedCount}` : 'Данные успешно обновлены');
    } catch (e) {
      showError(`Ошибка обновления данных: ${extractNestJsErrorMessage(e)}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <CardPaper sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" my={2}>
        <Typography variant="body1" fontWeight={500}>
          Внести массовое изменение
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Выбрано: {selectedCount} лидов
        </Typography>
      </Stack>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        <Form>
          <Stack direction="row" spacing={2}>
            <Field component={LeadStatusPickerField} name="statusId" label="Статус" onItemChange={(item: { label: string } | null) => setChangeLabel('statusId', item?.label)} />
            <Field component={LeadBudgetPickerField} name="budgetId" label="Бюджет" onItemChange={(item: { label: string } | null) => setChangeLabel('budgetId', item?.label)} />
            <Field label="Качество" component={LeadQualityPickerField} name="qualityId" onItemChange={(item: { label: string } | null) => setChangeLabel('qualityId', item?.label)} />
            <Field
              label="Менеджер"
              component={UserPickerField}
              name="userId"
              clearable={true}
              withNonSelectOption={true}
              onItemChange={(item: { id: number; firstName: string; secondName: string } | null) => setChangeLabel('userId', item ? (item.id === 0 ? 'Не выбран' : `${item.firstName} ${item.secondName}`) : undefined)}
            />
            <Button sx={{ minWidth: '120px' }} color="warning" variant="contained" type="submit" autoFocus>
              Применить
            </Button>
          </Stack>
        </Form>
      </Formik>
      <Dialog open={Boolean(pendingUpdate)} onClose={() => !isUpdating && setPendingUpdate(null)} fullWidth maxWidth="sm">
        <DialogTitle>Вы действительно хотите выполнить это действие?</DialogTitle>
        <DialogContent dividers>
          <Typography>Будут изменены {selectedCount} лидов:</Typography>
          <List dense disablePadding sx={{ mt: 1 }}>
            {pendingUpdate?.changes.map((change) => (
              <ListItem key={change.label} disableGutters>
                <ListItemText primary={`${change.label}: ${change.value}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setPendingUpdate(null)} disabled={isUpdating}>Отмена</Button>
          <Button variant="contained" color="warning" onClick={handleConfirm} disabled={isUpdating} autoFocus>
            {isUpdating ? 'Выполняется...' : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>
    </CardPaper>
  );
};

export const LeadBulkUpdate = memo(LeadUpdate);
